import React, { useEffect, useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { Button, TextField } from '@mui/material';
import DatePicker from 'react-datepicker';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';

import CheckIcon from '@mui/icons-material/Check';
import 'react-datepicker/dist/react-datepicker.css';
import { EditAddress } from './EditAddress';

import {
  updatingUserInfo,
  updateUserInfo,
  createUserResidencyInfo,
  patchUserResidencyInfo,
  getCsrfToken,
} from '../../actions';

import {
  FIRST_NAME,
  LAST_NAME,
  EMAIL,
  PHONE,
  DATE_OF_BIRTH,
  SAVE,
  CANCEL,
  firstname,
  lastname,
  email,
  phone,
  birthdate,
  messages,
} from '../../constants/Profile/userProfileEdit';

import {
  STREET_ADDRESS,
  APT_UNIT,
  CITY,
  STATE,
  ZIP_CODE,
} from '../../constants/Profile/editAddress';

import './UserProfileEdit.css';

export const UserProfileEdit = ({ user, csrfToken, userAddress, onCancelEdit, token, onSave }) => {
  const [userProfile, setUserProfile] = useState(user);
  const [currentUserCurrent, setCurrentUserAddress] = useState(userAddress);

  const dispatch = useDispatch();

  const {
    handleSubmit,
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      [firstname]: userProfile.firstname ?? '',
      [lastname]: userProfile.lastname ?? '',
      [phone]: userProfile.phone ?? '',
      [birthdate]: dayjs(userProfile.birthdate).format('MM/DD/YYYY') ?? '',
    },
  });

  useEffect(() => {
    if (!csrfToken) {
      dispatch(getCsrfToken());
    }
  }, [csrfToken]);

  const handleUserProfileChange = ({ name, value }) => {
    setUserProfile((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });

    mixpanel.track('Profile Field Edited', {
      userId: user.id,
      field: name,
      section: 'User Profile',
    });
  };

  const handleAddressChange = ({ name, value }) => {
    setCurrentUserAddress((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });

    mixpanel.track('Profile Field Edited', {
      userId: user.id,
      field: name,
      section: 'User Address',
    });
  };

  const onSubmit = () => {
    const values = getValues();
    const formattedBirthdate = dayjs(values.birthdate).format('MM/DD/YYYY');
    values[birthdate] = formattedBirthdate;

    setUserProfile((prev) => ({
      ...prev,
      firstname: values.firstname,
      lastname: values.lastname,
      phone: values.phone,
      birthdate: formattedBirthdate,
    }));

    dispatch((dispatch) => updatingUserInfo(dispatch));
    dispatch(
      updateUserInfo({
        token,
        id: user.id,
        userInfo: {
          firstname: values[firstname],
          lastname: values[lastname],
          phone: values[phone],
          birthdate: values[birthdate],
        },
      }),
    );

    if (userAddress.userAddressId) {
      dispatch(
        patchUserResidencyInfo({
          id: userAddress.userAddressId,
          body: {
            ...currentUserCurrent,
            [STREET_ADDRESS]: currentUserCurrent[STREET_ADDRESS],
            [APT_UNIT]: currentUserCurrent[APT_UNIT],
            [CITY]: currentUserCurrent[CITY],
            [STATE]: currentUserCurrent[STATE],
            [ZIP_CODE]: currentUserCurrent[ZIP_CODE],
          },
          tokenValue: token,
        }),
      );
    } else {
      dispatch(
        createUserResidencyInfo({
          body: {
            ...currentUserCurrent,
            [STREET_ADDRESS]: currentUserCurrent[STREET_ADDRESS],
            [APT_UNIT]: currentUserCurrent[APT_UNIT],
            [CITY]: currentUserCurrent[CITY],
            [STATE]: currentUserCurrent[STATE],
            [ZIP_CODE]: currentUserCurrent[ZIP_CODE],
          },
          user,
          tokenValue: token,
        }),
      );
    }

    onSave();
  };

  return (
    <div className="user-profile-edit-wrapper">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="first-name-last-name-wrapper">
          <div className="first-name-field-wrapper">
            <label>{FIRST_NAME}</label> <br />
            <TextField
              id="inputfield"
              placeholder={FIRST_NAME}
              name={firstname}
              fullWidth
              {...register(firstname, {
                required: messages.firstNameRequired,
              })}
              variant="outlined"
            />
            {errors.firstname && <p className="error-message">{errors.firstname.message}</p>}
          </div>
          <div className="last-name-field-wrapper">
            <label>{LAST_NAME}</label> <br />
            <TextField
              id="inputfield"
              placeholder={LAST_NAME}
              name={lastname}
              fullWidth
              {...register(lastname, {
                required: messages.lastNameRequired,
              })}
              variant="outlined"
            />
            {errors.lastname && <p className="error-message">{errors.lastname.message}</p>}
          </div>
        </div>

        <div className="email-phone-wrapper">
          <div className="email-field-wrapper">
            <label>{EMAIL}</label> <br />
            <TextField
              id="inputfield"
              placeholder={EMAIL}
              name={email}
              fullWidth
              value={userProfile[email]}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              onChange={(e) => {
                handleUserProfileChange({ name: email, value: e.target.value });
              }}
              variant="outlined"
            />
          </div>
          <div className="phone-field-wrapper">
            <label>{PHONE}</label> <br />
            <TextField
              id="inputfield"
              placeholder={PHONE}
              name={phone}
              fullWidth
              {...register(phone, {
                required: messages.phoneNumberRequired,
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: messages.phoneNumberValid,
                },
              })}
              onChange={(e) => {
                handleUserProfileChange({ name: 'phone', value: e.target.value });
              }}
              variant="outlined"
            />
            {errors.phone && <p className="error-message">{errors.phone.message}</p>}
          </div>
        </div>
        <div className="date-of-birth-field-wrapper">
          <Controller
            name={birthdate}
            control={control}
            rules={{
              required: messages.birthdayRequired,
              validate: (value) => dayjs().diff(value, 'year') >= 18 || messages.ageRequirement,
            }}
            render={({ field, fieldState }) => (
              <div className="date-picker-wrapper">
                <label>{DATE_OF_BIRTH}</label>
                <DatePicker
                  id="datepicker"
                  showYearDropdown
                  scrollableYearDropdown
                  showMonthDropdown
                  scrollableMonthDropdown
                  yearDropdownItemNumber={100}
                  minDate={new Date(1900, 0, 1)}
                  placeholderText="mm/dd/yyyy"
                  value={
                    userProfile.birthdate ? dayjs(userProfile.birthdate).format('MM/DD/YYYY') : ''
                  }
                  maxDate={new Date()}
                  onChange={(date) => {
                    handleUserProfileChange({
                      name: birthdate,
                      value: dayjs(date).format('MM/DD/YYYY'),
                    });
                    field.onChange(date);
                  }}
                />
                {fieldState.error && <p className="error-message">{fieldState.error.message}</p>}
              </div>
            )}
          />
        </div>

        <EditAddress
          address={currentUserCurrent}
          onAddressChange={handleAddressChange}
          register={register}
          errors={errors}
          setValue={setValue}
        />
        <div className="actionBtns">
          <Button onClick={onCancelEdit} color="error" variant="contained" id="cancelBtn">
            {CANCEL}
          </Button>

          <Button type="submit" variant="contained" id="submitBtn">
            <CheckIcon /> {SAVE}
          </Button>
        </div>
      </form>
    </div>
  );
};
